// ---------------------------------------------------------------------------
// Live Monitor — real-time attendance feed (interactive).
// Streams punch-in events over /topic/attendance and animates them in.
// ---------------------------------------------------------------------------
import React, {useMemo, useState} from "react";
import {
    Avatar,
    Badge,
    Box,
    Button,
    Circle,
    Flex,
    Grid,
    HStack,
    ScaleFade,
    Switch,
    Text,
    useColorModeValue,
} from "@chakra-ui/react";
import {FaBroadcastTower, FaRegTrashAlt} from "react-icons/fa";

import Card from "../../../../../components/Card/Card.js";
import CardBody from "../../../../../components/Card/CardBody.js";
import CardHeader from "../../../../../components/Card/CardHeader.js";
import AttendanceStatCard from "../../components/AttendanceStatCard";
import StatusBadge from "../../components/StatusBadge";
import useAttendanceSocket from "../../socket/useAttendanceSocket";
import {ATTENDANCE_STATUS} from "../../constants/AttendanceConstants";
import {formatTime} from "../../utils/attendanceUtils";

function LiveAttendance() {
    const textColor = useColorModeValue("gray.700", "white");
    const rowBg = useColorModeValue("gray.50", "gray.700");
    const [enabled, setEnabled] = useState(true);

    const {events, connected, mode, clear} = useAttendanceSocket(enabled);

    const stats = useMemo(() => {
        const present = events.filter(
            (e) => e.status === ATTENDANCE_STATUS.PRESENT
        ).length;
        const late = events.filter((e) => e.status === ATTENDANCE_STATUS.LATE).length;
        return {total: events.length, present, late};
    }, [events]);

    return (
        <Flex direction="column">
            <Grid
                templateColumns={{sm: "1fr 1fr", lg: "repeat(4, 1fr)"}}
                gap="16px"
                mb="20px"
            >
                <AttendanceStatCard
                    title="Live Check-ins"
                    value={stats.total}
                    hint="this session"
                    color="teal.300"
                />
                <AttendanceStatCard title="On Time" value={stats.present} color="green.400" />
                <AttendanceStatCard title="Late" value={stats.late} color="orange.400" />
                <AttendanceStatCard
                    title="Feed"
                    value={mode === "live" ? "Live" : "Simulated"}
                    hint={mode === "live" ? "backend socket" : "demo stream"}
                    color={mode === "live" ? "green.400" : "purple.400"}
                    icon={<FaBroadcastTower color="white" />}
                />
            </Grid>

            <Card>
                <CardHeader p="16px 0 16px 0">
                    <Flex justify="space-between" align="center" wrap="wrap" gap="12px" w="100%">
                        <HStack>
                            <Circle
                                size="12px"
                                bg={connected ? "green.400" : "gray.400"}
                                boxShadow={connected ? "0 0 0 4px rgba(72,187,120,0.2)" : "none"}
                            />
                            <Text fontSize="lg" color={textColor} fontWeight="bold">
                                Live Attendance Monitor
                            </Text>
                            <Badge colorScheme={connected ? "green" : "gray"} borderRadius="6px">
                                {connected ? "Connected" : "Offline"}
                            </Badge>
                        </HStack>

                        <HStack spacing="16px">
                            <HStack>
                                <Text fontSize="sm" color="gray.500">
                                    Stream
                                </Text>
                                <Switch
                                    isChecked={enabled}
                                    onChange={(e) => setEnabled(e.target.checked)}
                                    colorScheme="teal"
                                />
                            </HStack>
                            <Button
                                leftIcon={<FaRegTrashAlt />}
                                size="sm"
                                variant="ghost"
                                onClick={clear}
                                isDisabled={!events.length}
                            >
                                Clear
                            </Button>
                        </HStack>
                    </Flex>
                </CardHeader>

                <CardBody>
                    <Box w="100%" maxH="520px" overflowY="auto">
                        {events.length === 0 ? (
                            <Flex direction="column" align="center" py="50px" gap="8px">
                                <FaBroadcastTower size="28px" color="#A0AEC0" />
                                <Text color="gray.400">
                                    {enabled
                                        ? "Waiting for check-ins…"
                                        : "Stream paused. Toggle it on to resume."}
                                </Text>
                            </Flex>
                        ) : (
                            events.map((evt) => (
                                <ScaleFade key={evt.id} initialScale={0.9} in>
                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        bg={rowBg}
                                        borderRadius="12px"
                                        p="12px 16px"
                                        mb="10px"
                                    >
                                        <HStack spacing="12px">
                                            <Avatar size="sm" name={evt.studentName} />
                                            <Box>
                                                <Text fontSize="sm" fontWeight="bold" color={textColor}>
                                                    {evt.studentName}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500">
                                                    {evt.className} · Roll #{evt.rollNo}
                                                </Text>
                                            </Box>
                                        </HStack>
                                        <HStack spacing="14px">
                                            <StatusBadge status={evt.status} size="sm" />
                                            <Text fontSize="sm" color="gray.500" minW="70px" textAlign="right">
                                                {formatTime(evt.markedAt)}
                                            </Text>
                                        </HStack>
                                    </Flex>
                                </ScaleFade>
                            ))
                        )}
                    </Box>
                </CardBody>
            </Card>
        </Flex>
    );
}

export default LiveAttendance;
